import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { addHullPolygonsToMap, getObstaclesToRender } from '../../utils/hullMapLayer';
import { T } from '../../assets/theme';
import { useSurfaceStyles } from '../../assets/SurfaceStyleContext';
import { DraggableModal, ResizeHandle } from '../../components/UI';
import { useResizableHeight } from '../../utils/useResizableHeight';
import { getObstacleIcon } from '../../utils/markers';
import { AIRSPACE_CLASS_META, styleForAirspace, formatAirspaceLimit } from '../../utils/airspaceStyle';
import { assessProjectAirspace, saveAirspaceScreenshot, getAirspaceScreenshot } from '../../api/client';
import MapMeasureTool from '../../components/MapMeasureTool';
import MapCoordinateViewer from '../../components/MapCoordinateViewer';
import { useShield } from '../../utils/useShield';
import ShieldControl from '../../components/ShieldControl';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const TILE_LAYERS = {
    dark: { label: '🌙 Dark', url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', attribution: '© CARTO' },
    street: { label: '🗺 Street', url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', attribution: '© OSM' },
    satellite: { label: '🛰 Satellite', url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', attribution: '© Esri' },
};

const EMPTY_RESULT = { buffer_km: 0.3, obstacle_count: 0, buffer_geometry: null, airspaces: [] };

// Describe which obstacles trigger a match: all of them, a named subset, or just the nearest (not inside).
function describeObstaclesInside(a) {
    const inside = a.obstacles_inside || [];
    if (inside.length === 0) return null;
    if (a.all_obstacles_inside) return 'All obstacles inside';
    return `Obstacles inside: ${inside.map(o => o.name).join(', ')}`;
}

export default function AirspaceAssessmentPage({ project, obstacles, loading: projectLoading, state, setState, showObstacles = true, showObsLabels, onObstacleContextMenu, onMapReady }) {
    const { getStyle } = useSurfaceStyles();
    const bufferStyle = getStyle('airspace.buffer');
    const mapRef = useRef(null);
    const mapInst = useRef(null);
    const tileRef = useRef(null);
    const layersRef = useRef([]);

    const [mapMode, setMapMode] = useState('street');
    const [mapState, setMapState] = useState(null);
    const [result, setResult] = useState(state?.airspaceResult || EMPTY_RESULT);
    // Step 2 reference set — this project's own obstacles that actually fall
    // inside an assessed airspace boundary, across every airspace checked —
    // not just the project's tallest point overall, which could be nowhere
    // near any of them.
    const penetratingObstacleIds = useMemo(() => (
        (result.airspaces || []).flatMap(a => (a.obstacles_inside || []).map(o => o.id))
    ), [result]);
    const { shieldOn, toggleShield, shieldData, shieldLoading, shieldError, projectMaxTotalElev, projectPenetratingMaxTotalElev, radiusKm, setRadiusKm, exportCsv: exportShieldCsv } = useShield(mapInst, obstacles, penetratingObstacleIds);
    const [visibleUuids, setVisibleUuids] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [activeUuid, setActiveUuid] = useState(null);
    const [resultsCollapsed, setResultsCollapsed] = useState(false);
    const [resultsHeight, startResultsResize] = useResizableHeight(300, { min: 120, max: 700 });
    const [capturing, setCapturing] = useState(false);
    const [hasScreenshot, setHasScreenshot] = useState(false);
    const [resultsPoppedOut, setResultsPoppedOut] = useState(false);

    // CTR/TMA are switched on independently of the general airspace list —
    // both default off, since this also drives the vertical "operational
    // service volume" obstruction check below (a heavier, opt-in analysis).
    const [typeSwitch, setTypeSwitch] = useState({ CTR: false, TMA: false });
    // Overrides each CTR/TMA's own published floor as the obstruction
    // threshold — e.g. FAOR's 7000 ft AMSL circuit altitude — when set.
    const [operationalAltOverride, setOperationalAltOverride] = useState(state?.airspaceOperationalAltOverride ?? '');

    // Load the airspace assessment for this project
    const loadAssessment = useCallback(async (overrideFt) => {
        if (!project?.id) return;
        setLoading(true);
        setError('');
        try {
            const { data } = await assessProjectAirspace(project.id, undefined, overrideFt);
            setResult(data || EMPTY_RESULT);
            setVisibleUuids(new Set((data?.airspaces || []).map(a => a.uuid)));
            if (data?.airspaces?.length > 0) {
                setActiveUuid(data.airspaces[0].uuid);
            }
        } catch (err) {
            console.error('Failed to load Airspace assessment', err);
            setError(err.response?.data?.detail || err.message);
        } finally {
            setLoading(false);
        }
    }, [project?.id]);

    useEffect(() => { loadAssessment(operationalAltOverride); }, [loadAssessment]); // eslint-disable-line react-hooks/exhaustive-deps

    // Re-fetch with the operational altitude override — debounced so typing
    // a value doesn't fire a request per keystroke.
    useEffect(() => {
        const t = setTimeout(() => { loadAssessment(operationalAltOverride); }, 600);
        return () => clearTimeout(t);
    }, [operationalAltOverride]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (setState) setState(prev => ({ ...prev, airspaceOperationalAltOverride: operationalAltOverride }));
    }, [operationalAltOverride, setState]);

    // Check if a map screenshot already exists
    useEffect(() => {
        if (!project?.id) return;
        getAirspaceScreenshot(project.id)
            .then(() => setHasScreenshot(true))
            .catch(() => setHasScreenshot(false));
    }, [project?.id]);

    // Sync results back to parent assessment state so tab switches don't refetch
    useEffect(() => {
        if (result && setState) {
            setState(prev => {
                if (JSON.stringify(prev.airspaceResult) === JSON.stringify(result)) return prev;
                return { ...prev, airspaceResult: result };
            });
        }
    }, [result, setState]);

    // Initialize Leaflet map
    useEffect(() => {
        if (!mapRef.current || mapInst.current) return;
        mapInst.current = L.map(mapRef.current, { center: [-28.5, 25.0], zoom: 6, zoomDelta: 0.7, zoomSnap: 0.7 });
        tileRef.current = L.tileLayer(TILE_LAYERS[mapMode].url, { attribution: TILE_LAYERS[mapMode].attribution, maxZoom: 24, maxNativeZoom: 19 }).addTo(mapInst.current);

        const ro = new ResizeObserver(() => mapInst.current?.invalidateSize());
        ro.observe(mapRef.current);
        setMapState(mapInst.current);
        onMapReady?.(mapInst.current);

        return () => {
            ro.disconnect();
            mapInst.current?.remove();
            mapInst.current = null;
            tileRef.current = null;
            setMapState(null);
            onMapReady?.(null);
        };
    }, []);

    // Tile mode toggle
    useEffect(() => {
        if (!mapInst.current) return;
        if (tileRef.current) mapInst.current.removeLayer(tileRef.current);
        const layer = TILE_LAYERS[mapMode];
        tileRef.current = L.tileLayer(layer.url, { attribution: layer.attribution, maxZoom: 24, maxNativeZoom: 19 }).addTo(mapInst.current);
    }, [mapMode]);

    const activeAirspace = result.airspaces.find(a => a.uuid === activeUuid);

    // CTR/TMA need their own type switch on top of the general per-airspace
    // checkbox — every other type is governed by visibleUuids alone.
    const isAirspaceVisible = useCallback((a) => (
        visibleUuids.has(a.uuid) && (a.type !== 'CTR' && a.type !== 'TMA' ? true : typeSwitch[a.type])
    ), [visibleUuids, typeSwitch]);

    const obstructionAirspaces = useMemo(() => (
        (result.airspaces || []).filter(a => (a.type === 'CTR' || a.type === 'TMA') && typeSwitch[a.type] && a.any_obstruction)
    ), [result, typeSwitch]);

    const allVisible = result.airspaces.length > 0 && visibleUuids.size === result.airspaces.length;
    const someVisible = visibleUuids.size > 0 && !allVisible;

    const toggleAllAirspace = (checked) => {
        setVisibleUuids(checked ? new Set(result.airspaces.map(a => a.uuid)) : new Set());
    };

    const toggleAirspace = (uuid, checked) => {
        setVisibleUuids(prev => {
            const next = new Set(prev);
            if (checked) next.add(uuid); else next.delete(uuid);
            return next;
        });
    };

    // Draw map layers (buffer hull, airspaces, obstacles)
    useEffect(() => {
        if (!mapInst.current || loading || projectLoading) return;

        layersRef.current.forEach(layer => mapInst.current.removeLayer(layer));
        layersRef.current = [];

        const bounds = [];

        // 1. The hull buffer — one shared shape for the whole assessment
        if (result.buffer_geometry) {
            const bufferLayer = L.geoJSON(result.buffer_geometry, {
                style: {
                    color: bufferStyle.color, weight: 1.5, fillColor: bufferStyle.color,
                    fillOpacity: bufferStyle.opacity, dashArray: '6,5',
                },
                interactive: false,
            }).addTo(mapInst.current);
            layersRef.current.push(bufferLayer);
            const bb = bufferLayer.getBounds();
            bounds.push(bb.getSouthWest(), bb.getNorthEast());
        }

        // 2. Matched airspace polygons that are toggled visible, active one emphasized
        result.airspaces.forEach(a => {
            if (!a.geometry || !isAirspaceVisible(a)) return;
            const isActive = a.uuid === activeUuid;
            const sty = styleForAirspace(a);
            try {
                const layer = L.geoJSON(a.geometry, {
                    style: {
                        color: sty.color,
                        fillColor: sty.fillColor,
                        weight: isActive ? 3 : sty.weight,
                        opacity: sty.opacity,
                        fillOpacity: isActive ? Math.min(sty.fillOpacity + 0.2, 0.55) : sty.fillOpacity,
                    },
                    onEachFeature: (feature, lyr) => {
                        const upper = formatAirspaceLimit(a.upper_limit, a.upper_limit_uom, a.upper_limit_ref);
                        const lower = formatAirspaceLimit(a.lower_limit, a.lower_limit_uom, a.lower_limit_ref);
                        lyr.bindTooltip(a.name || 'Airspace', { sticky: true, className: 'airspace-tt' });
                        lyr.bindPopup(
                            `<b>${a.name}</b><br/><small style="color:#999">${sty._class}</small><br/>` +
                            `<span style="font-size:11px">Upper: ${upper}<br/>Lower: ${lower}` +
                            (a.frequency ? `<br/>${a.frequency}` : '') + `</span>`
                        );
                        lyr.on('click', () => setActiveUuid(a.uuid));
                    },
                }).addTo(mapInst.current);
                layersRef.current.push(layer);
                if (isActive) {
                    const b = layer.getBounds();
                    bounds.push(b.getSouthWest(), b.getNorthEast());
                }
            } catch (e) {
                console.error('Failed to render airspace geometry', a.uuid, e);
            }
        });

        // 3. Project obstacles — highlight every obstacle inside the active airspace
        const activeInsideIds = new Set((activeAirspace?.obstacles_inside || []).map(o => o.id));
        const activeIsNearestOnly = activeAirspace && activeInsideIds.size === 0;
        getObstaclesToRender(obstacles, project?.id, showObstacles).forEach(o => {
            if (o.latitude == null || o.longitude == null) return;
            const ll = [o.latitude, o.longitude];
            const isInside = activeInsideIds.has(o.id);
            const isNearest = activeIsNearestOnly && activeAirspace?.nearest_obstacle_id === o.id;
            const status = activeAirspace ? ((isInside || isNearest) ? 'penetrated' : undefined) : undefined;
            const icon = getObstacleIcon(L, o, status, showObsLabels);

            const m = L.marker(ll, { icon })
                .bindPopup(`<b>${o.name || 'Unnamed Obstacle'}</b><br/>ID: ${o.id}<br/>ELEVATION: ${o.elevation_m ?? '—'} m`);
            if (onObstacleContextMenu) {
                m.on('contextmenu', e => {
                    e.originalEvent.preventDefault();
                    onObstacleContextMenu(o, e.originalEvent.clientX, e.originalEvent.clientY);
                });
            }
            m.addTo(mapInst.current);
            layersRef.current.push(m);

            if (isInside || isNearest) bounds.push(ll);
        });
        addHullPolygonsToMap(mapInst.current, obstacles, project?.id, layersRef);

        if (bounds.length > 0 && mapInst.current) {
            mapInst.current.fitBounds(L.latLngBounds(bounds), { padding: [50, 50], maxZoom: 12 });
        }
    }, [result, activeUuid, activeAirspace, obstacles, loading, projectLoading, showObstacles, showObsLabels, visibleUuids, typeSwitch, isAirspaceVisible, onObstacleContextMenu, bufferStyle]);

    // Map viewport capture (mirrors RNAV Routes / CNS BRA screenshot capture)
    const handleCapture = async () => {
        if (!mapInst.current || !project?.id) return;
        setCapturing(true);
        try {
            const container = mapRef.current;
            const containerRect = container.getBoundingClientRect();
            const w = Math.round(containerRect.width);
            const h = Math.round(containerRect.height);

            const canvas = document.createElement('canvas');
            canvas.width = w; canvas.height = h;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = '#07111e';
            ctx.fillRect(0, 0, w, h);

            const tileEls = [...container.querySelectorAll('.leaflet-tile:not(.leaflet-tile-loading)')];
            await Promise.all(tileEls.map(async tile => {
                if (!tile.src) return;
                const tRect = tile.getBoundingClientRect();
                const tx = Math.floor(tRect.left - containerRect.left);
                const ty = Math.floor(tRect.top - containerRect.top);
                const tw = Math.ceil(tRect.width) + 1;
                const th = Math.ceil(tRect.height) + 1;
                try {
                    const resp = await fetch(tile.src, { mode: 'cors' });
                    if (!resp.ok) return;
                    const blob = await resp.blob();
                    const url = URL.createObjectURL(blob);
                    await new Promise(resolve => {
                        const img = new Image();
                        img.onload = () => { ctx.drawImage(img, tx, ty, tw, th); URL.revokeObjectURL(url); resolve(); };
                        img.onerror = () => { URL.revokeObjectURL(url); resolve(); };
                        img.src = url;
                    });
                } catch {}
            }));

            const svgEl = container.querySelector('.leaflet-overlay-pane svg');
            if (svgEl) {
                const svgRect = svgEl.getBoundingClientRect();
                const sx = Math.round(svgRect.left - containerRect.left);
                const sy = Math.round(svgRect.top - containerRect.top);
                const clone = svgEl.cloneNode(true);
                clone.style.transform = ''; clone.style.left = '0'; clone.style.top = '0';
                const svgStr = new XMLSerializer().serializeToString(clone);
                const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
                const url = URL.createObjectURL(blob);
                await new Promise(resolve => {
                    const img = new Image();
                    img.onload = () => { ctx.drawImage(img, sx, sy, Math.round(svgRect.width), Math.round(svgRect.height)); URL.revokeObjectURL(url); resolve(); };
                    img.onerror = () => { URL.revokeObjectURL(url); resolve(); };
                    img.src = url;
                });
            }

            const markerEls = [...container.querySelectorAll('.leaflet-marker-icon')];
            for (const marker of markerEls) {
                const mRect = marker.getBoundingClientRect();
                const x = Math.round(mRect.left - containerRect.left);
                const y = Math.round(mRect.top - containerRect.top);
                const img = marker.querySelector('img');
                if (img && img.complete && img.naturalWidth) {
                    try { ctx.drawImage(img, x, y, Math.round(mRect.width), Math.round(mRect.height)); } catch {}
                }
            }

            const dataUrl = canvas.toDataURL('image/jpeg', 0.88);
            await saveAirspaceScreenshot(project.id, dataUrl);
            setHasScreenshot(true);
            alert('✓ Airspace Map captured successfully!');
        } catch (err) {
            console.error('Capture failed:', err);
            alert(`✕ Failed to capture map screenshot: ${err.message}`);
        } finally {
            setCapturing(false);
        }
    };

    const exportCsv = () => {
        if (!result.airspaces.length) return;
        const headers = ["Airspace Name", "Designator", "Class", "Upper Limit", "Lower Limit", "Frequency", "Obstacles", "Distance (m)"];
        const rows = result.airspaces.map(a => [
            a.name, a.designator, a.type,
            formatAirspaceLimit(a.upper_limit, a.upper_limit_uom, a.upper_limit_ref),
            formatAirspaceLimit(a.lower_limit, a.lower_limit_uom, a.lower_limit_ref),
            a.frequency || '', describeObstaclesInside(a) || a.nearest_obstacle_name || '', a.distance_m ?? '',
        ]);
        const csvContent = [
            headers.join(","),
            ...rows.map(r => r.map(val => `"${String(val ?? '').replace(/"/g, '""')}"`).join(","))
        ].join("\n");

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `${(project?.name || 'Project').replace(/[\/\\]/g, '_')}_Airspace_Assessment.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const renderResultsContent = () => (
        <div style={{ overflowY: 'auto', flex: 1, padding: 12 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, textAlign: 'left' }}>
                <thead>
                    <tr style={{ background: T.bg0, borderBottom: `2px solid ${T.border}` }}>
                        <th style={{ padding: '6px 8px', color: T.muted }}>Airspace</th>
                        <th style={{ padding: '6px 8px', color: T.muted }}>Class</th>
                        <th style={{ padding: '6px 8px', color: T.muted, textAlign: 'center' }}>Upper</th>
                        <th style={{ padding: '6px 8px', color: T.muted, textAlign: 'center' }}>Lower</th>
                        <th style={{ padding: '6px 8px', color: T.muted }}>Frequency</th>
                        <th style={{ padding: '6px 8px', color: T.muted }}>Obstacles</th>
                        <th style={{ padding: '6px 8px', color: T.muted, textAlign: 'center' }}>Distance</th>
                    </tr>
                </thead>
                <tbody>
                    {result.airspaces.map(a => {
                        const meta = AIRSPACE_CLASS_META[a.type] || AIRSPACE_CLASS_META.Other;
                        const isActive = a.uuid === activeUuid;
                        return (
                            <tr key={a.uuid}
                                onClick={() => setActiveUuid(a.uuid)}
                                style={{
                                    cursor: 'pointer',
                                    borderBottom: `1px solid ${T.border}15`,
                                    background: isActive ? 'rgba(91,168,217,0.08)' : 'transparent',
                                }}
                            >
                                <td style={{ padding: '6px 8px', color: T.white, fontWeight: isActive ? 700 : 400 }}>{a.name}</td>
                                <td style={{ padding: '6px 8px' }}>
                                    <span style={{
                                        fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                                        color: meta.color, background: meta.fillColor + '33', border: `1px solid ${meta.color}55`,
                                    }}>{a.type}</span>
                                </td>
                                <td style={{ padding: '6px 8px', color: T.white, textAlign: 'center' }}>
                                    {formatAirspaceLimit(a.upper_limit, a.upper_limit_uom, a.upper_limit_ref)}
                                </td>
                                <td style={{ padding: '6px 8px', color: T.white, textAlign: 'center' }}>
                                    {formatAirspaceLimit(a.lower_limit, a.lower_limit_uom, a.lower_limit_ref)}
                                </td>
                                <td style={{ padding: '6px 8px', color: T.muted, fontSize: 10 }}>{a.frequency || '—'}</td>
                                <td style={{ padding: '6px 8px', color: describeObstaclesInside(a) ? '#ff5f5f' : T.white, fontWeight: describeObstaclesInside(a) ? 700 : 400 }}>
                                    {describeObstaclesInside(a) || a.nearest_obstacle_name || '—'}
                                </td>
                                <td style={{ padding: '6px 8px', color: describeObstaclesInside(a) ? '#ff5f5f' : T.white, textAlign: 'center', fontWeight: describeObstaclesInside(a) ? 700 : 400 }}>
                                    {describeObstaclesInside(a) ? 'Inside' : a.distance_m != null ? `${(a.distance_m / 1000).toFixed(2)} km` : '—'}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {(typeSwitch.CTR || typeSwitch.TMA) && (
                <div style={{ marginTop: 20 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: T.blueLt, marginBottom: 8 }}>
                        🚧 Obstruction to Operational Service Volume (CTR/TMA)
                    </div>
                    {obstructionAirspaces.length === 0 ? (
                        <div style={{ fontSize: 11, color: T.muted }}>
                            No project obstacle reaches the {operationalAltOverride ? 'operational altitude override' : "aerodrome's default operational floor (or the airspace's published floor where none is known)"} of any switched-on CTR/TMA.
                        </div>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, textAlign: 'left' }}>
                            <thead>
                                <tr style={{ background: T.bg0, borderBottom: `2px solid ${T.border}` }}>
                                    <th style={{ padding: '6px 8px', color: T.muted }}>Airspace</th>
                                    <th style={{ padding: '6px 8px', color: T.muted }}>Obstacle</th>
                                    <th style={{ padding: '6px 8px', color: T.muted, textAlign: 'center' }}>Top Elev (ft AMSL)</th>
                                    <th style={{ padding: '6px 8px', color: T.muted, textAlign: 'center' }}>Floor (ft AMSL)</th>
                                    <th style={{ padding: '6px 8px', color: T.muted }}>Floor Source</th>
                                    <th style={{ padding: '6px 8px', color: T.muted, textAlign: 'center' }}>Excess (ft)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {obstructionAirspaces.flatMap(a => (
                                    (a.obstacles_inside || []).filter(o => o.obstructs).map(o => (
                                        <tr key={`${a.uuid}_${o.id}`} style={{ borderBottom: `1px solid ${T.border}15`, background: 'rgba(232,64,64,0.10)' }}>
                                            <td style={{ padding: '6px 8px', color: T.white, fontWeight: 700 }}>{a.name}</td>
                                            <td style={{ padding: '6px 8px', color: T.white }}>{o.name}</td>
                                            <td style={{ padding: '6px 8px', color: T.white, textAlign: 'center' }}>{o.top_elev_ft_amsl?.toFixed(0)}</td>
                                            <td style={{ padding: '6px 8px', color: T.white, textAlign: 'center' }}>{a.obstruction_floor_ft?.toFixed(0)}</td>
                                            <td style={{ padding: '6px 8px', color: T.muted, fontSize: 10 }}>{a.obstruction_floor_source}</td>
                                            <td style={{ padding: '6px 8px', color: '#ff5f5f', fontWeight: 700, textAlign: 'center' }}>{Math.abs(o.clearance_ft).toFixed(0)}</td>
                                        </tr>
                                    ))
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </div>
    );

    return (
        <div style={{ display: 'flex', flex: 1, height: '100%', minHeight: 0, background: T.bg2, position: 'relative' }}>
            {/* Sidebar */}
            <div style={{
                width: 320, borderRight: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column',
                background: T.bg1, zIndex: 10, flexShrink: 0
            }}>
                <div style={{ padding: 16, borderBottom: `1px solid ${T.border}33` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: T.white, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Airspace Assessment
                        </div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: allVisible ? T.white : T.muted, cursor: 'pointer', userSelect: 'none' }}>
                            <input
                                type="checkbox"
                                checked={allVisible}
                                ref={el => { if (el) el.indeterminate = someVisible; }}
                                onChange={e => toggleAllAirspace(e.target.checked)}
                                style={{ accentColor: T.blue, cursor: 'pointer' }}
                            />
                            🛂 Airspace
                        </label>
                    </div>
                    <div style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>
                        Published airspace overlapping a {result.buffer_km} km buffer around the obstacle field
                    </div>
                </div>

                <div style={{ padding: 16, borderBottom: `1px solid ${T.border}33` }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: T.muted, marginBottom: 8, letterSpacing: 0.5 }}>
                        AIRSPACE TYPE — OBSTRUCTION CHECK
                    </div>
                    <div style={{ display: 'flex', gap: 16, marginBottom: 10 }}>
                        {['CTR', 'TMA'].map(t => (
                            <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: typeSwitch[t] ? T.white : T.muted, cursor: 'pointer', userSelect: 'none' }}>
                                <input
                                    type="checkbox"
                                    checked={typeSwitch[t]}
                                    onChange={e => setTypeSwitch(prev => ({ ...prev, [t]: e.target.checked }))}
                                    style={{ accentColor: T.blue, cursor: 'pointer' }}
                                />
                                {t}
                            </label>
                        ))}
                    </div>
                    {(typeSwitch.CTR || typeSwitch.TMA) && (
                        <div>
                            <label style={{ fontSize: 10, color: T.muted, display: 'block', marginBottom: 4 }}>
                                Operational altitude override (ft AMSL) — most CTR floors are published as SFC (0 ft), which no aircraft actually flies at. Known aerodromes (e.g. FAOR, 7000 ft circuit altitude) already default to a real operational floor; set this only to override that default or to supply one where none is known.
                            </label>
                            <input
                                type="number"
                                value={operationalAltOverride}
                                onChange={e => setOperationalAltOverride(e.target.value)}
                                placeholder="Use aerodrome default / published floor"
                                style={{ width: '100%', padding: '6px 8px', borderRadius: 6, background: T.bg0, border: `1px solid ${T.border}`, color: T.white, fontSize: 12 }}
                            />
                        </div>
                    )}
                </div>

                {loading ? (
                    <div style={{ padding: 24, textAlign: 'center', color: T.muted, fontSize: 12 }}>
                        Loading airspace…
                    </div>
                ) : error ? (
                    <div style={{ padding: 16, color: T.danger, fontSize: 11 }}>
                        Error: {error}
                    </div>
                ) : result.airspaces.length === 0 ? (
                    <div style={{ padding: 24, textAlign: 'center', color: T.muted, fontSize: 12 }}>
                        No published airspace overlaps a {result.buffer_km} km buffer around this project's obstacles.
                    </div>
                ) : (
                    <div style={{ flex: 1, overflowY: 'auto', padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {result.airspaces.map(a => {
                            const meta = AIRSPACE_CLASS_META[a.type] || AIRSPACE_CLASS_META.Other;
                            const isActive = a.uuid === activeUuid;
                            const isVisible = isAirspaceVisible(a);
                            return (
                                <div
                                    key={a.uuid}
                                    onClick={() => setActiveUuid(a.uuid)}
                                    style={{
                                        padding: 12, borderRadius: 8, cursor: 'pointer',
                                        border: isActive ? '1px solid var(--blue-glass-border)' : '1px solid transparent',
                                        background: isActive ? 'var(--blue-glass-fill)' : 'transparent',
                                        opacity: isVisible ? 1 : 0.45,
                                        transition: 'all 0.15s'
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden' }}>
                                            <input
                                                type="checkbox"
                                                checked={isVisible}
                                                onClick={e => e.stopPropagation()}
                                                onChange={e => toggleAirspace(a.uuid, e.target.checked)}
                                                style={{ accentColor: T.blue, cursor: 'pointer', flexShrink: 0 }}
                                            />
                                            <span style={{ fontWeight: 600, color: isActive ? T.blueLt : T.white, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {a.name}
                                            </span>
                                        </div>
                                        <span style={{
                                            flexShrink: 0, fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                                            color: meta.color, background: meta.fillColor + '33', border: `1px solid ${meta.color}55`,
                                        }}>{a.type}</span>
                                    </div>
                                    <div style={{ fontSize: 11, color: T.muted, marginTop: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <span>Upper: <strong style={{ color: T.white }}>{formatAirspaceLimit(a.upper_limit, a.upper_limit_uom, a.upper_limit_ref)}</strong> · Lower: <strong style={{ color: T.white }}>{formatAirspaceLimit(a.lower_limit, a.lower_limit_uom, a.lower_limit_ref)}</strong></span>
                                        <span>
                                            {describeObstaclesInside(a)
                                                ? <span style={{ color: '#ff5f5f', fontWeight: 700 }}>⚠ {describeObstaclesInside(a)}</span>
                                                : <span>Nearest obstacle {(a.distance_m / 1000).toFixed(2)} km away</span>}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                <div style={{ padding: 12, borderTop: `1px solid ${T.border}33`, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ fontSize: 11, color: T.muted, textAlign: 'center' }}>
                        {result.obstacle_count} obstacle{result.obstacle_count !== 1 ? 's' : ''} · {result.airspaces.length} airspace{result.airspaces.length !== 1 ? 's' : ''} found
                    </div>
                    <button
                        onClick={handleCapture}
                        disabled={capturing || result.airspaces.length === 0}
                        style={{
                            width: '100%', padding: '8px 12px', border: 'none', borderRadius: 6,
                            background: capturing ? T.muted : T.blue, color: T.white, fontSize: 11,
                            fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
                        }}
                    >
                        📸 {capturing ? 'Capturing Map...' : 'Capture Map Screenshot'}
                    </button>
                    {hasScreenshot && (
                        <div style={{ fontSize: 10, color: T.safe, textAlign: 'center' }}>
                            ✓ Saved map screenshot will render in Report.
                        </div>
                    )}
                </div>
            </div>

            {/* Map & Results Panel */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
                    <ShieldControl top={92} shieldOn={shieldOn} toggleShield={toggleShield} shieldData={shieldData} shieldLoading={shieldLoading} shieldError={shieldError} projectMaxTotalElev={projectMaxTotalElev} projectPenetratingMaxTotalElev={projectPenetratingMaxTotalElev} radiusKm={radiusKm} setRadiusKm={setRadiusKm} exportCsv={exportShieldCsv} />
                    <MapMeasureTool map={mapState} top={52} tableOffset={result.airspaces.length ? (resultsCollapsed ? 40 : 300) : 0} />
                    <MapCoordinateViewer map={mapState} />
                    <div ref={mapRef} style={{ width: '100%', height: '100%', zIndex: 1 }} />

                    <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 100, display: 'flex', gap: 4, background: T.bg1, padding: 4, borderRadius: 8, border: `1px solid ${T.border}` }}>
                        {Object.entries(TILE_LAYERS).map(([k, l]) => (
                            <button
                                key={k}
                                onClick={() => setMapMode(k)}
                                style={{
                                    padding: '4px 10px', borderRadius: 6, border: 'none', fontSize: 11, cursor: 'pointer',
                                    background: mapMode === k ? 'rgba(58, 138, 196, 0.15)' : 'transparent',
                                    color: mapMode === k ? T.blueLt : T.muted, fontWeight: mapMode === k ? 700 : 400
                                }}
                            >
                                {l.label}
                            </button>
                        ))}
                    </div>
                </div>

                {result.airspaces.length > 0 && (
                    <div style={{
                        background: T.bg1, borderTop: `1px solid ${T.border}`,
                        display: 'flex', flexDirection: 'column',
                        height: resultsCollapsed ? 40 : resultsHeight,
                        transition: 'height 0.25s ease-in-out',
                        overflow: 'hidden'
                    }}>
                        {!resultsCollapsed && <ResizeHandle onMouseDown={startResultsResize} />}
                        <div style={{
                            padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            cursor: 'pointer', borderBottom: `1px solid ${T.border}22`, background: T.bg2
                        }} onClick={() => setResultsCollapsed(!resultsCollapsed)}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 13, fontWeight: 700, color: T.blueLt }}>
                                    Overlapping Airspace
                                </span>
                                <span style={{ fontSize: 11, background: T.border, color: T.white, padding: '1px 6px', borderRadius: 10 }}>
                                    {result.airspaces.length}
                                </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                {!resultsPoppedOut && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setResultsPoppedOut(true); setResultsCollapsed(true); }}
                                        style={{ background: 'none', border: 'none', color: T.blueLt, cursor: 'pointer', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600 }}
                                        title="Pop out results to a movable window"
                                    >
                                        🗖 Pop Out
                                    </button>
                                )}
                                <button
                                    onClick={(e) => { e.stopPropagation(); exportCsv(); }}
                                    style={{ padding: '2px 8px', borderRadius: 4, border: 'none', fontSize: 11, cursor: 'pointer', background: T.blue, color: T.white, fontWeight: 700 }}
                                >
                                    📥 Export CSV
                                </button>
                                <span style={{ color: T.muted, fontSize: 12 }}>{resultsCollapsed ? '▲' : '▼'}</span>
                            </div>
                        </div>

                        <div style={{ flex: 1, overflowY: 'auto' }}>
                            {resultsPoppedOut ? (
                                <div style={{ padding: 20, textAlign: 'center', color: T.muted, fontSize: 13 }}>
                                    Results are popped out.
                                    <button
                                        onClick={() => { setResultsPoppedOut(false); setResultsCollapsed(false); }}
                                        style={{ background: 'none', border: 'none', color: T.blue, textDecoration: 'underline', cursor: 'pointer', marginLeft: 6, fontWeight: 600 }}
                                    >
                                        Bring back
                                    </button>
                                </div>
                            ) : renderResultsContent()}
                        </div>
                    </div>
                )}
            </div>

            {resultsPoppedOut && result.airspaces.length > 0 && (
                <DraggableModal
                    title={`${project?.name || ''} - Overlapping Airspace`}
                    onClose={() => setResultsPoppedOut(false)}
                    width={900}
                    height={500}
                >
                    {renderResultsContent()}
                </DraggableModal>
            )}
        </div>
    );
}
